import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MediaImage } from '@/components/generals/media-image'

type Crumb = {
    label: ReactNode
    href?: string
}

interface PageHeroProps {
    eyebrow: string
    title: ReactNode
    description?: ReactNode
    breadcrumbs: Array<Crumb>
    image?: {
        src: string
        alt: string
        className?: string
    }
    children?: ReactNode
}

export const PageHero: FC<PageHeroProps> = ({
    eyebrow,
    title,
    description,
    breadcrumbs,
    image,
    children
}): ReactNode => {
    return (
        <section aria-labelledby='page-title' className='relative overflow-clip bg-brand-cream'>
            <div aria-hidden='true' className='pointer-events-none absolute -top-32 -right-32 size-[26rem] rounded-full bg-brand-orange/10 blur-3xl' />

            <div className={cn('relative mx-auto grid max-w-7xl gap-12 px-5 pt-10 pb-16 sm:px-6 lg:px-10 lg:pt-14 lg:pb-20', image && 'lg:grid-cols-12 lg:items-center')}>
                <div className={cn(image ? 'lg:col-span-7' : 'max-w-3xl')}>
                    <nav aria-label='Breadcrumb' className='animate-in fade-in fill-mode-both duration-500'>
                        <ol className='flex flex-wrap items-center gap-1.5 text-[13px] text-brand-stone'>
                            {breadcrumbs.map((crumb, index) => {
                                const isLast = index === breadcrumbs.length - 1

                                return (
                                    <li key={index} className='inline-flex items-center gap-1.5'>
                                        {crumb.href && !isLast
                                            ? (
                                                <Link href={crumb.href} className='transition-colors hover:text-brand-charcoal'>
                                                    {crumb.label}
                                                </Link>
                                            )
                                            : (
                                                <span aria-current={isLast ? 'page' : undefined} className={cn(isLast && 'font-medium text-brand-charcoal')}>
                                                    {crumb.label}
                                                </span>
                                            )}

                                        {!isLast && <ChevronRight className='size-3.5 text-brand-gold' aria-hidden='true' />}
                                    </li>
                                )
                            })}
                        </ol>
                    </nav>

                    <p className='mt-8 inline-flex items-center gap-3 text-[0.72rem] font-semibold tracking-[0.3em] text-brand-ember uppercase animate-in fade-in slide-in-from-bottom-3 fill-mode-both duration-700'>
                        <span aria-hidden='true' className='h-px w-8 bg-brand-gold' />
                        {eyebrow}
                    </p>

                    <h1
                        id='page-title'
                        className='mt-5 font-heading text-[2.9rem] leading-[1] font-semibold tracking-tight text-brand-charcoal sm:text-6xl lg:text-[4.4rem] animate-in fade-in slide-in-from-bottom-4 fill-mode-both delay-100 duration-700'
                    >
                        {title}
                    </h1>

                    {description && (
                        <div className='mt-6 max-w-2xl text-[1.05rem] leading-relaxed text-brand-stone sm:text-lg animate-in fade-in slide-in-from-bottom-4 fill-mode-both delay-200 duration-700'>
                            {description}
                        </div>
                    )}

                    {children && (
                        <div className='mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap animate-in fade-in slide-in-from-bottom-4 fill-mode-both delay-300 duration-700'>
                            {children}
                        </div>
                    )}
                </div>

                {image && (
                    <div className='relative mx-auto w-full max-w-md lg:col-span-5 lg:max-w-none animate-in fade-in zoom-in-95 fill-mode-both delay-200 duration-1000'>
                        <div aria-hidden='true' className='absolute inset-0 translate-x-3 translate-y-3 rounded-[2rem] rounded-tl-[8rem] border border-brand-gold/60' />

                        <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] rounded-tl-[8rem] bg-brand-sand shadow-[0_40px_80px_-40px_rgba(32,32,32,0.55)] lg:aspect-[5/6]'>
                            <MediaImage
                                src={image.src}
                                alt={image.alt}
                                preload
                                sizes='(min-width: 1024px) 40vw, (min-width: 640px) 28rem, 100vw'
                                className={image.className}
                            />
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}
